import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { BarChart2, TrendingUp, FileText, RefreshCw, ExternalLink, Sparkles } from 'lucide-react'
import { apiFetch } from '../../config/api'
import { Link } from 'react-router-dom'

interface PageStat {
  path: string
  today: string
  last7d: string
}

const PATH_LABELS: Record<string, string> = {
  '/': 'Startseite',
  '/brunch': 'Brunch',
  '/galerie': 'Galerie',
  '/kontakt': 'Kontakt',
  '/geschichte': 'Geschichte',
  '/impressum': 'Impressum',
  '/datenschutz': 'Datenschutz',
  '/jobs': 'Jobs',
  '/veranstaltung': 'Veranstaltung',
  '/veranstaltungen': 'Veranstaltungen',
  '/raeume': 'Räume',
  '/partys': 'Partys',
  '/partys-karneval': 'Partys Karneval',
  '/hochzeiten': 'Hochzeiten',
  '/unternehmensfeiern': 'Unternehmensfeiern',
  '/private-feiern': 'Private Feiern',
  '/buffets-angebote': 'Buffets & Angebote',
  '/foodtruck': 'Foodtruck',
}

function getPageLabel(path: string): string {
  const normalized = path.replace(/^\//, '/') || '/'
  return PATH_LABELS[normalized] ?? (normalized === '/' ? 'Startseite' : normalized)
}

export default function DashboardStatistik() {
  const [pages, setPages] = useState<PageStat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getAuthToken = () => localStorage.getItem('dashboard_token') || ''

  const fetchStats = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiFetch('/analytics/pages', {
        headers: { Authorization: getAuthToken() },
      })
      if (!res.ok) {
        if (res.status === 401) return
        throw new Error('Statistiken konnten nicht geladen werden.')
      }
      const data = await res.json()
      if (data.success && Array.isArray(data.pages)) {
        setPages(
          data.pages.map((p: { path: string; today: string | number; last7d: string | number }) => ({
            path: p.path || '/',
            today: String(p.today ?? 0),
            last7d: String(p.last7d ?? 0),
          }))
        )
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  const totalToday = pages.reduce((sum, p) => sum + parseInt(p.today, 10) || 0, 0)
  const total7d = pages.reduce((sum, p) => sum + parseInt(p.last7d, 10) || 0, 0)
  const max7d = Math.max(1, ...pages.map((p) => parseInt(p.last7d, 10) || 0))

  return (
    <div>
      <motion.div
        className="mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-600/20 to-brand-600/20 border border-brand-500/30 rounded-full px-4 py-2 mb-4"
            >
              <BarChart2 className="w-4 h-4 text-brand-400" />
              <span className="text-sm font-medium text-brand-400">Seiten-Performance</span>
            </motion.div>
            <h1 className="font-serif text-5xl md:text-6xl font-light text-white mb-3 bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-brand-400">
              Statistik
            </h1>
            <p className="text-gray-300 text-lg mb-4">
              Aufrufe pro Seite – so performen deine Inhalte.
            </p>
            <div className="h-1 w-32 bg-gradient-to-r from-brand-500 via-brand-500 to-brand-500 rounded-full shadow-lg shadow-brand-500/50" />
          </div>
          <motion.button
            onClick={fetchStats}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-brand-600/20 border border-white/10 hover:border-brand-500/30 text-gray-300 hover:text-brand-400 transition-all disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </motion.button>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl border border-brand-500/20"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 rounded-xl bg-brand-600/20">
              <Sparkles className="w-5 h-5 text-brand-400" />
            </div>
            <span className="text-gray-400 text-sm font-medium">Heute</span>
          </div>
          <p className="text-4xl font-bold text-white">{loading ? '—' : totalToday}</p>
          <p className="text-gray-400 text-sm mt-1">Seitenaufrufe gesamt</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl border border-brand-500/20"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 rounded-xl bg-brand-600/20">
              <TrendingUp className="w-5 h-5 text-brand-400" />
            </div>
            <span className="text-gray-400 text-sm font-medium">Letzte 7 Tage</span>
          </div>
          <p className="text-4xl font-bold text-white">{loading ? '—' : total7d}</p>
          <p className="text-gray-400 text-sm mt-1">Seitenaufrufe gesamt</p>
        </motion.div>
      </div>

      {/* Error */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400"
        >
          {error}
        </motion.div>
      )}

      {/* Page List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="rounded-2xl bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl border border-white/10 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <FileText className="w-5 h-5 text-brand-400" />
          <h2 className="font-serif text-xl font-light text-white">Seiten im Überblick</h2>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-70" />
            <p>Statistiken werden geladen …</p>
          </div>
        ) : pages.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <BarChart2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Noch keine Aufrufdaten vorhanden.</p>
            <p className="text-sm mt-1">Sobald Besucher die Website nutzen, erscheinen hier die Zahlen.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-sm border-b border-white/5">
                  <th className="px-6 py-4 font-medium">Seite</th>
                  <th className="px-6 py-4 font-medium text-right w-28">Heute</th>
                  <th className="px-6 py-4 font-medium text-right w-32">7 Tage</th>
                  <th className="px-6 py-4 font-medium w-48 pl-4">Anteil</th>
                  <th className="px-6 py-4 w-20" />
                </tr>
              </thead>
              <tbody>
                {pages.map((row, index) => {
                  const todayNum = parseInt(row.today, 10) || 0
                  const last7dNum = parseInt(row.last7d, 10) || 0
                  const share = max7d > 0 ? (last7dNum / max7d) * 100 : 0
                  const pathForLink = (row.path && row.path !== '/') ? row.path : '/'
                  return (
                    <motion.tr
                      key={row.path + index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * index }}
                      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-medium text-white">
                            {getPageLabel(row.path)}
                          </span>
                          <span className="text-gray-500 text-xs font-mono">{row.path || '/'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-white font-semibold tabular-nums">{row.today}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-brand-400 font-semibold tabular-nums">{row.last7d}</span>
                      </td>
                      <td className="px-6 py-4 pl-4">
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden max-w-[140px]">
                          <motion.div
                            className="h-full bg-gradient-to-r from-brand-500 to-brand-400 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${share}%` }}
                            transition={{ duration: 0.6, delay: 0.1 * index }}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Link
                          to={pathForLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-gray-400 hover:text-brand-400 text-sm transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Öffnen
                        </Link>
                      </td>
                    </motion.tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  )
}
